import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useAdminStore } from '@/stores/admin'

/**
 * Requires the player to be authenticated.
 * Redirects to /login with a redirect query param if not.
 */
export async function requireAuth(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const userStore = useUserStore()
  await userStore.waitForInit()

  if (!userStore.isAuthenticated) {
    return next({ path: '/login', query: { redirect: to.fullPath } })
  }

  next()
}

/**
 * Only allows unauthenticated users (login/register pages).
 * Redirects authenticated users to /player/lobby.
 */
export async function requireGuest(
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const userStore = useUserStore()
  await userStore.waitForInit()

  if (userStore.isAuthenticated) {
    return next({ path: '/player/lobby' })
  }

  next()
}

/**
 * Requires the player to have a verified email.
 * Redirects to /verify-email if not verified.
 * Must be used after requireAuth (assumes user is authenticated).
 */
export async function requireVerified(
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const userStore = useUserStore()
  await userStore.waitForInit()

  if (!userStore.isAuthenticated) {
    return next({ path: '/login' })
  }

  if (!userStore.isEmailVerified) {
    return next({ path: '/verify-email' })
  }

  next()
}

/**
 * Only allows unauthenticated admin users (admin login page).
 * Redirects authenticated admins to /admin/dashboard.
 */
export async function requireAdminGuest(
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const adminStore = useAdminStore()
  await adminStore.waitForInit()

  if (adminStore.isAuthenticated) {
    return next({ path: '/admin/dashboard' })
  }

  next()
}

/**
 * Checks if the admin user is authenticated.
 * Redirects to /admin/login if not logged in.
 */
export async function requireAdmin(
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const adminStore = useAdminStore()
  await adminStore.waitForInit()

  if (!adminStore.isAuthenticated) {
    return next({ name: 'admin.login' })
  }

  next()
}

/**
 * Checks if the admin user has the required role(s) and/or permission(s)
 * defined on route.meta.requireRole and route.meta.requirePermission.
 * Redirects to /admin/unauthorized if requirements are not met.
 */
export async function requireRole(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const adminStore = useAdminStore()
  await adminStore.waitForInit()

  // Check admin authentication first
  if (!adminStore.isAuthenticated) {
    return next({ name: 'admin.login' })
  }

  // Check required roles (user must have one of the listed roles)
  const requiredRoles = to.meta.requireRole ?? []
  if (requiredRoles.length > 0 && !adminStore.hasRole(requiredRoles)) {
    return next({ name: 'admin.unauthorized' })
  }

  // Check required permissions (user must have at least one of the listed permissions)
  const requiredPermissions = to.meta.requirePermission ?? []
  if (requiredPermissions.length > 0 && !adminStore.hasPermission(requiredPermissions)) {
    return next({ name: 'admin.unauthorized' })
  }

  next()
}
